import questionPaperService from '../../../services/questionPaperService';

/**
 * Render the question-paper preview to a multi-page A4 PDF.
 *
 * The preview is split on screen into `[data-paper-page]` A4 sheets (only the
 * active one is visible). Here we render EACH sheet to its own PDF page, so the
 * on-screen pages map 1:1 to PDF pages — no blind canvas slicing that could cut
 * a question across a page boundary.
 *
 * WHY html-to-image (not html2canvas): the paper contains KaTeX math whose
 * fractions/roots are laid out with a CSS *table* (`.vlist-t`/`.vlist-r`/`.vlist`
 * = inline-table/table-row/table-cell + `vertical-align:bottom`) and zero-height
 * children offset by `top`. html2canvas has weak CSS-table support and rounds
 * those offsets away, so fraction bars drift ABOVE the numerator on export. Every
 * html2canvas workaround (block/inline-block/absolute transforms, its own
 * `foreignObjectRendering`) either broke the baseline or rendered blank. Verified
 * in-browser: html-to-image serialises the node into an SVG `<foreignObject>` and
 * lets the browser's OWN layout engine paint it, so math renders pixel-perfect —
 * exactly like the on-screen preview.
 *
 * @param {HTMLElement} element  the preview root (contains the page sheets)
 * @param {string} filename      output file name (with or without .pdf)
 */

// Inline cross-origin question images (files.medhabi.bd / files.sohozkaj.com /
// the Medhabi Firebase bucket that hosts MCQ option figures) as data: URLs via
// the backend CORS proxy. Those hosts send no CORS header, so html-to-image's
// toSvg can't fetch them and the whole export fails ("Failed to fetch"). On
// error the original src is kept (only that one image is skipped).
async function inlineRemoteImages(root) {
    const imgs = [...root.querySelectorAll('img')].filter((img) => {
        const src = img.getAttribute('src') || '';
        return /^https?:\/\/(files\.medhabi\.bd|files\.sohozkaj\.com|firebasestorage\.googleapis\.com)\//i.test(src);
    });
    await Promise.all(imgs.map(async (img) => {
        try {
            const dataUrl = await questionPaperService.proxyImageDataUrl(img.src);
            img.setAttribute('src', dataUrl);
            img.removeAttribute('crossorigin');
            await img.decode().catch(() => {});
        } catch { /* keep original src — that image is skipped, rest still exports */ }
    }));
}

export async function generateQuestionPaperPdf(element, filename, pageFormat = 'a4', contentPx) {
    if (!element) throw new Error('Preview element is not ready.');

    // Load the heavy PDF/render libs on demand (keeps them out of the eager bundle).
    const [{ toSvg }, { jsPDF }] = await Promise.all([
        import('html-to-image'),
        import('jspdf'),
    ]);

    // WHY we rasterise the SVG ourselves instead of calling toCanvas():
    // html-to-image's toCanvas() loads the serialised SVG through its internal
    // createImage(), which does `img.decode().then(...)` with NO `.catch`. On the
    // large (multi-MB) font-embedded SVG data URL Chrome intermittently *rejects*
    // decode(), so that promise never settles and the whole export hangs forever
    // (endless spinner → the user sees "download failed"). We keep toSvg() — its
    // SVG serialisation renders KaTeX/Bengali pixel-perfectly — but load the image
    // with our own robust loader: onload/onerror + a hard timeout and no bare
    // decode(). A data-URL src with crossOrigin='anonymous' keeps the canvas
    // untainted (blob-URL SVGs taint it and block toDataURL).
    const loadImage = (url) => new Promise((resolve, reject) => {
        const img = new Image();
        const timer = setTimeout(() => reject(new Error('PDF rendering timed out.')), 20000);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('PDF rendering failed.')); };
        img.crossOrigin = 'anonymous';
        img.src = url;
    });

    // KaTeX + Bengali fonts load lazily. If the user exports before they finish,
    // the capture uses fallback glyphs with wrong metrics. Wait for whatever is in
    // flight so the paper renders with the real fonts.
    if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* non-fatal */ }
    }

    // On-screen sheet width the DOM is laid out at — passed in so the export
    // matches exactly what the user sees (WYSIWYG). Falls back to A4/A5 defaults.
    // 'joint' = A4 landscape holding two A5-width page columns side by side.
    const isJoint = pageFormat === 'joint';
    const CONTENT_PX = Math.round(contentPx) || (isJoint ? 1024 : pageFormat === 'a5' ? 512 : 720);

    const pageNodes = element.querySelectorAll('[data-paper-page]');
    const nodes = pageNodes.length ? [...pageNodes] : [element];

    const pdf = new jsPDF({
        orientation: isJoint ? 'landscape' : 'portrait',
        unit: 'mm',
        format: isJoint ? 'a4' : pageFormat,
    });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const imgW = pageW - margin * 2;
    const maxImgH = pageH - margin * 2;

    for (let i = 0; i < nodes.length; i += 1) {
        const clone = nodes[i].cloneNode(true);
        // Un-hide inactive pages and normalize to the fixed A4 content width.
        // Joint sheets are flex rows (two page columns) — keep that layout.
        clone.classList.remove('hidden');
        clone.style.display = isJoint ? 'flex' : 'block';
        clone.style.width = `${CONTENT_PX}px`;
        clone.style.transform = 'none';
        clone.style.maxWidth = 'none';
        // Strip screen-only affordances (drag handles) so they don't print.
        clone.querySelectorAll('[data-dnd-handle]').forEach((el) => el.remove());

        // Drop optional header fields (e.g. Time) that were left blank.
        clone.querySelectorAll('[data-export-optional]').forEach((el) => {
            const input = el.querySelector('input[data-export-text]');
            if (input && !input.value.trim()) el.remove();
        });

        // Replace inline-editable header inputs with plain text so no input
        // chrome (caret, box) appears in the exported paper. The span must keep
        // the input's inline styles (template font size / line height) and its
        // inline-block display: on a plain inline span `width` is ignored, so
        // the w-full institution and exam-title lines would shrink to text
        // width and merge onto ONE line in the export.
        clone.querySelectorAll('input[data-export-text]').forEach((el) => {
            const span = document.createElement('span');
            span.textContent = el.value.trim() || el.getAttribute('data-export-fallback') || '';
            span.className = el.className;
            span.style.cssText = el.style.cssText;
            span.style.display = 'inline-block';
            el.replaceWith(span);
        });

        const wrapper = document.createElement('div');
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.style.cssText = [
            'position:fixed',
            'left:-20000px',
            'top:0',
            `width:${CONTENT_PX}px`,
            'background:#ffffff',
            'overflow:visible',
            'pointer-events:none',
            'z-index:-1',
        ].join(';');
        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);

        try {
            const RATIO = 2;                // ~2× DPI for crisp text/print
            // Embed cross-origin question images (via CORS proxy) before rasterising,
            // else toSvg can't fetch them and the export fails.
            // eslint-disable-next-line no-await-in-loop
            await inlineRemoteImages(clone);
            // eslint-disable-next-line no-await-in-loop
            const svgUrl = await toSvg(clone, {
                backgroundColor: '#ffffff',
                cacheBust: true,            // avoid stale cached images (e.g. logo)
                width: CONTENT_PX,
            });
            // eslint-disable-next-line no-await-in-loop
            const img = await loadImage(svgUrl);

            const canvas = document.createElement('canvas');
            canvas.width = CONTENT_PX * RATIO;
            canvas.height = Math.max(1, img.naturalHeight) * RATIO;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            let drawW = imgW;
            let drawH = (canvas.height * imgW) / canvas.width;
            // Safety: if a single page's content is a touch taller than A4, scale
            // it down to fit rather than clipping.
            if (drawH > maxImgH) {
                drawH = maxImgH;
                drawW = (canvas.width * drawH) / canvas.height;
            }
            const offsetX = margin + (imgW - drawW) / 2;
            const imgData = canvas.toDataURL('image/jpeg', 0.95);

            if (i > 0) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', offsetX, margin, drawW, drawH);
        } finally {
            wrapper.remove();
        }
    }

    const name = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
    pdf.save(name);
}
